import React, { useState, useEffect, useCallback } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  Box, HStack, Flex, Button, Input, Heading, useToast,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, FormControl, FormLabel,
  Text, Menu, MenuButton, MenuList, MenuItem, ButtonGroup,
  OrderedList, ListItem, Image
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { SettingsIcon, RepeatIcon, CheckCircleIcon, DownloadIcon, ChevronDownIcon, ExternalLinkIcon, AttachmentIcon } from '@chakra-ui/icons';
import SiteLevelSection from '../components/calculator/SiteLevelSection';
import DesignSection from '../components/calculator/DesignSection';
import ConstructionSection from '../components/calculator/ConstructionSection';
import DrillingComplexitySection from '../components/calculator/DrillingComplexitySection';
import WellControlSection from '../components/calculator/WellControlSection';
import ScoreChart from '../components/calculator/ScoreChart';
import { formDataState, defaultFormData, scoresSelector, assessmentState } from '../state/calculatorState';
import { settingsState } from '../state/settingsState';
import { assessmentService } from '../services/api';
import { finalHoleDiameterOptions } from '../utils/complexityCalculator';
import { generatePDFContent, openPrintWindow } from '../utils/pdfGenerator';

// Convert string to Title Case
const toTitleCase = (str) => {
  if (!str) return str;
  return str.replace(/\w\S*/g, (txt) =>
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

const shimmer = keyframes`
  0% { left: -50%; }
  100% { left: 150%; }
`;

const spinGear = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const CalculatorPage = () => {
  const [formData, setFormData] = useRecoilState(formDataState);
  const [currentAssessment, setCurrentAssessment] = useRecoilState(assessmentState);
  const scores = useRecoilValue(scoresSelector);
  const settings = useRecoilValue(settingsState);
  const [assessmentName, setAssessmentName] = useState('');
  const [isExportingPDF] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const { isOpen: isSaveOpen, onOpen: onSaveOpen, onClose: onSaveClose } = useDisclosure();
  const { isOpen: isParamsOpen, onOpen: onParamsOpen, onClose: onParamsClose } = useDisclosure();
  const [isPulsing, setIsPulsing] = useState(false);
  const { isOpen: isIntroOpen, onOpen: onIntroOpen, onClose: onIntroClose } = useDisclosure();
  const toast = useToast();

  // Show intro modal on first visit
  useEffect(() => {
    if (!localStorage.getItem('introSeen')) {
      onIntroOpen();
    }
  }, [onIntroOpen]);

  const handleIntroClose = () => {
    localStorage.setItem('introSeen', 'true');
    onIntroClose();
  };

  // Pulse the Set Parameters button every 30 seconds
  useEffect(() => {
    const triggerPulse = () => {
      setIsPulsing(true);
      setTimeout(() => setIsPulsing(false), 1500);
    };

    // Pulse once on load
    triggerPulse();

    // Then pulse every 10 seconds
    const interval = setInterval(triggerPulse, 10000);

    return () => clearInterval(interval);
  }, []);

  const handleUnitToggle = (newUnits) => {
    setFormData(prev => {
      if (prev.units === newUnits) return prev;
      const toImperial = newUnits === 'imperial';
      return {
        ...prev,
        units: newUnits,
        verticalDepth: Math.round(toImperial ? prev.verticalDepth * 3.28084 : prev.verticalDepth / 3.28084),
        maxTemperature: Math.round(toImperial ? prev.maxTemperature * 9 / 5 + 32 : (prev.maxTemperature - 32) * 5 / 9),
        maxPressure: Math.round(toImperial ? prev.maxPressure * 14.5038 : prev.maxPressure / 14.5038)
      };
    });
  };

  const handleReset = () => {
    setFormData(defaultFormData);
    setCurrentAssessment({ id: null, name: '' });
    setLastSaved(null);
  };

  // Save existing assessment (update)
  const saveCurrentAssessment = useCallback(async () => {
    if (!currentAssessment.id) return false;

    setIsSaving(true);
    try {
      await assessmentService.update(currentAssessment.id, {
        formData,
        units: formData.units
      });
      setLastSaved(new Date());
      return true;
    } catch (err) {
      toast({ title: 'Save failed', status: 'error', duration: 3000 });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [currentAssessment.id, formData, toast]);

  // Autosave every 60 seconds when editing an existing assessment
  useEffect(() => {
    if (!currentAssessment.id) return;

    const interval = setInterval(() => {
      saveCurrentAssessment();
    }, 60000);

    return () => clearInterval(interval);
  }, [currentAssessment.id, saveCurrentAssessment]);

  const handleSaveAssessment = async () => {
    // If editing an existing assessment, just update it
    if (currentAssessment.id) {
      const success = await saveCurrentAssessment();
      if (success) {
        toast({ title: 'Assessment saved', status: 'success', duration: 2000 });
      }
      return;
    }

    // New assessment - need a name
    if (!assessmentName.trim()) {
      toast({ title: 'Please enter a name', status: 'warning', duration: 2000 });
      return;
    }

    try {
      const response = await assessmentService.create({
        name: assessmentName,
        formData,
        units: formData.units
      });

      // Track the newly created assessment
      setCurrentAssessment({ id: response.data.assessment.id, name: assessmentName });
      setLastSaved(new Date());

      toast({ title: 'Assessment saved', status: 'success', duration: 2000 });
      setAssessmentName('');
      onSaveClose();
    } catch (err) {
      toast({ title: 'Save failed', status: 'error', duration: 3000 });
    }
  };

  const getDisplayValue = (key, value) => {
    const lookups = {
      location: settings.locationValues,
      rigCapacity: settings.rigCapacityValues,
      circulatingTemp: settings.circulationValues,
      pressureRegime: settings.pressRegimeValues,
      ucs: settings.ucsValues,
      ddi: settings.ddiValues,
      phase: settings.phaseValues
    };
    if (lookups[key] && lookups[key][value]) {
      return lookups[key][value].txt;
    }
    if (key === 'finalHoleDiameter') {
      const opt = finalHoleDiameterOptions.find(o => o.value === value);
      return opt ? opt.label : value;
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return value;
  };

  const handleExportCSV = () => {
    setIsExportingCSV(true);
    try {
      const assessmentTitle = currentAssessment.name || 'Unsaved Assessment';
      const totalScore = ((scores.siteScore + scores.wellScore) / 2 * 100).toFixed(1);

      const rows = [
        ['IADC Geothermal Well Complexity Index Report'],
        ['Generated', new Date().toLocaleString()],
        ['Assessment', assessmentTitle],
        ['Units', formData.units === 'metric' ? 'Metric' : 'Imperial'],
        [],
        ['SCORES'],
        ['Overall Complexity', `${totalScore}%`],
        ['Site Level Score', `${(scores.siteScore * 100).toFixed(1)}%`],
        ['Well Level Score', `${(scores.wellScore * 100).toFixed(1)}%`],
        [],
        ['SITE LEVEL BREAKDOWN'],
        ['Location Sensitivity', `${(scores.locationScore * 100).toFixed(1)}%`],
        ['Rig Capacity', `${(scores.rigScore * 100).toFixed(1)}%`],
        [],
        ['WELL LEVEL BREAKDOWN'],
        ['Design', `${(scores.dsgnScore * 100).toFixed(1)}%`],
        ['Construction', `${(scores.cnstrctnScore * 100).toFixed(1)}%`],
        ['Drilling Complexity', `${(scores.drlgCmplxScore * 100).toFixed(1)}%`],
        ['Well Control', `${(scores.wllCtrlScore * 100).toFixed(1)}%`],
        [],
        ['PARAMETERS'],
        ['Location Type', getDisplayValue('location', formData.location)],
        ['Near Residential', getDisplayValue('residential', formData.residential)],
        ['Near Sensitive Area', getDisplayValue('sensitive', formData.sensitive)],
        ['Rig Capacity', getDisplayValue('rigCapacity', formData.rigCapacity)],
        ['Re-Entry Well', getDisplayValue('reEntry', formData.reEntry)],
        ['Function', formData.Function],
        ['Final Hole Diameter', getDisplayValue('finalHoleDiameter', formData.finalHoleDiameter)],
        ['Number of Sections', formData.numberOfSections],
        ['Vertical Depth', `${formData.verticalDepth} ${formData.units === 'metric' ? 'm' : 'ft'}`],
        ['Max Temperature', `${formData.maxTemperature} ${formData.units === 'metric' ? 'C' : 'F'}`],
        ['Max Pressure', `${formData.maxPressure} ${formData.units === 'metric' ? 'bar' : 'psi'}`],
        ['Stimulation Required', getDisplayValue('stimulation', formData.stimulation)],
        ['Scaling Risk', getDisplayValue('scaling', formData.scaling)],
        ['Corrosion Risk', getDisplayValue('corrosion', formData.corrosion)],
        ['Circulating Temp', getDisplayValue('circulatingTemp', formData.circulatingTemp)],
        ['Pressure Regime', getDisplayValue('pressureRegime', formData.pressureRegime)],
        ['UBO/MPD', getDisplayValue('ubompd', formData.ubompd)],
        ['UCS', getDisplayValue('ucs', formData.ucs)],
        ['Interbedded Formations', getDisplayValue('interbedded', formData.interbedded)],
        ['DDI', getDisplayValue('ddi', formData.ddi)],
        ['Multilateral', getDisplayValue('multilateral', formData.multilateral)],
        ['Interception Required', getDisplayValue('interception', formData.interception)],
        ['Reservoir Phase', getDisplayValue('phase', formData.phase)],
        ['Hydrocarbons Present', getDisplayValue('hydroCarbons', formData.hydroCarbons)],
        ['Toxic Gas Present', getDisplayValue('toxicGas', formData.toxicGas)]
      ];

      const csvContent = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${assessmentTitle.replace(/[^a-z0-9]/gi, '-')}-complexity.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'CSV downloaded', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Export failed', status: 'error', duration: 3000 });
    } finally {
      setIsExportingCSV(false);
    }
  };

  // Create URL-friendly slug from name
  const slugify = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleExportPDF = async () => {
    // If editing an existing assessment, open print directly (no auto-save)
    if (currentAssessment.id) {
      const slug = slugify(currentAssessment.name);
      window.open(`/print/${slug}`, '_blank');
      return;
    }

    // No saved assessment - use inline HTML print
    const html = generatePDFContent({
      name: 'Unsaved Assessment',
      scores,
      formData,
      units: formData.units
    });
    openPrintWindow(html);
  };

  return (
    <Box>
      {/* Header Bar */}
      <HStack justify="space-between" mt="15px" mb={4} flexWrap="wrap" gap={3}>
        <Heading size="lg" color="gray.700" letterSpacing="0.5px">
          {toTitleCase(currentAssessment.name) || 'Well Complexity Index'}
        </Heading>

        <HStack spacing={3} flexWrap="wrap">
          {currentAssessment.id && lastSaved && (
            <Text fontSize="xs" color="gray.500">
              Saved {lastSaved.toLocaleTimeString()}
            </Text>
          )}
          <Button
            size="sm"
            leftIcon={<SettingsIcon sx={isPulsing ? { animation: `${spinGear} 0.6s ease-in-out` } : {}} />}
            onClick={onParamsOpen}
            bg="#94368d"
            color="white"
            _hover={{ bg: '#7a2d74' }}
            position="relative"
            overflow="hidden"
            sx={isPulsing ? {
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-50%',
                width: '50%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                animation: `${shimmer} 0.6s ease-in-out`,
              }
            } : {}}
          >
            Set Parameters
          </Button>
          <Button
            size="sm"
            leftIcon={<CheckCircleIcon />}
            onClick={currentAssessment.id ? handleSaveAssessment : onSaveOpen}
            bg="black"
            color="white"
            _hover={{ bg: 'gray.800' }}
            isLoading={isSaving}
          >
            {currentAssessment.id ? 'Save' : 'Save Assessment'}
          </Button>
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              variant="outline"
              leftIcon={<DownloadIcon />}
              rightIcon={<ChevronDownIcon />}
              isDisabled={!formData}
            >
              Export
            </MenuButton>
            <MenuList>
              <MenuItem icon={<ExternalLinkIcon />} onClick={handleExportPDF} isDisabled={isExportingPDF}>
                Print / PDF
              </MenuItem>
              <MenuItem icon={<AttachmentIcon />} onClick={handleExportCSV} isDisabled={isExportingCSV}>
                CSV
              </MenuItem>
            </MenuList>
          </Menu>
          <Button
            size="sm"
            onClick={handleReset}
            leftIcon={<RepeatIcon />}
            bg="red.500"
            color="white"
            _hover={{ bg: 'red.600' }}
          >
            Reset
          </Button>
        </HStack>
      </HStack>

      {/* Main Chart - Hero */}
      <ScoreChart />

      {/* Parameters Modal */}
      <Modal isOpen={isParamsOpen} onClose={onParamsClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxW={{ base: '95%', md: '950px' }}>
          <ModalHeader py={3} borderBottomWidth="1px" pr={12}>
            <HStack justify="space-between">
              <Text fontSize="md">Well Parameters</Text>
              <HStack spacing={3}>
                <ButtonGroup size="sm" isAttached variant="outline">
                  <Button
                    onClick={() => handleUnitToggle('metric')}
                    colorScheme={formData.units === 'metric' ? 'red' : 'gray'}
                    variant={formData.units === 'metric' ? 'solid' : 'outline'}
                  >
                    Metric
                  </Button>
                  <Button
                    onClick={() => handleUnitToggle('imperial')}
                    colorScheme={formData.units === 'imperial' ? 'red' : 'gray'}
                    variant={formData.units === 'imperial' ? 'solid' : 'outline'}
                  >
                    Imperial
                  </Button>
                </ButtonGroup>
                <Button size="xs" variant="ghost" onClick={handleReset} leftIcon={<RepeatIcon />}>
                  Reset All
                </Button>
              </HStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} bg="gray.50">
            <Flex direction={{ base: 'column', md: 'row' }} align="stretch">
              <Box flex={1} borderRight={{ base: 'none', md: '1px solid' }} borderRightColor={{ base: 'transparent', md: 'gray.200' }}>
                <Box borderBottom="1px solid" borderBottomColor="gray.200" p={4} bg="white">
                  <Text fontWeight="600" color="red.600" fontSize="xs" textTransform="uppercase" mb={3}>Site Level</Text>
                  <SiteLevelSection compact />
                </Box>
                <Box p={4} bg="white" borderBottom={{ base: '1px solid', md: 'none' }} borderBottomColor={{ base: 'gray.200', md: 'transparent' }}>
                  <Text fontWeight="600" color="blue.600" fontSize="xs" textTransform="uppercase" mb={3}>Well Control</Text>
                  <WellControlSection compact />
                </Box>
              </Box>
              <Box flex={1} borderRight={{ base: 'none', md: '1px solid' }} borderRightColor={{ base: 'transparent', md: 'gray.200' }}>
                <Box p={4} bg="white" borderBottom={{ base: '1px solid', md: 'none' }} borderBottomColor={{ base: 'gray.200', md: 'transparent' }}>
                  <Text fontWeight="600" color="blue.600" fontSize="xs" textTransform="uppercase" mb={3}>Design</Text>
                  <DesignSection compact />
                </Box>
              </Box>
              <Box flex={1}>
                <Box borderBottom="1px solid" borderBottomColor="gray.200" p={4} bg="white">
                  <Text fontWeight="600" color="blue.600" fontSize="xs" textTransform="uppercase" mb={3}>Construction</Text>
                  <ConstructionSection compact />
                </Box>
                <Box p={4} bg="white">
                  <Text fontWeight="600" color="blue.600" fontSize="xs" textTransform="uppercase" mb={3}>Drilling Complexity</Text>
                  <DrillingComplexitySection compact />
                </Box>
              </Box>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Save Assessment Modal */}
      <Modal isOpen={isSaveOpen} onClose={onSaveClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save Assessment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Assessment Name</FormLabel>
              <Input
                value={assessmentName}
                onChange={(e) => setAssessmentName(e.target.value)}
                placeholder="Enter assessment name"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSaveClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleSaveAssessment}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Intro Modal - first visit only */}
      <Modal isOpen={isIntroOpen} onClose={handleIntroClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={2}>
            <Image src="/img/iadc-logo.png" alt="IADC" h="28px" mb={3} />
            <Text fontSize="lg">Welcome to the Geothermal Well Complexity Index</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} color="gray.600">
              This tool calculates a complexity index for geothermal wells based on site conditions, well design, construction, drilling complexity, and well control factors.
            </Text>
            <Text fontWeight="600" mb={2}>How to get started:</Text>
            <OrderedList spacing={2} mb={4} color="gray.600" pl={2}>
              <ListItem>Click <Text as="span" fontWeight="600" color="gray.800">Set Parameters</Text> to enter your well data</ListItem>
              <ListItem>The complexity chart updates in real-time as you fill in values</ListItem>
              <ListItem><Text as="span" fontWeight="600" color="gray.800">Save</Text> your assessment to revisit or update it later</ListItem>
              <ListItem><Text as="span" fontWeight="600" color="gray.800">Export</Text> to PDF or CSV when you're ready to share</ListItem>
            </OrderedList>
            <Text fontSize="sm" color="gray.500">
              Scores range from 0% (simple) to 100% (highly complex) across Site Level and Well Level categories.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" onClick={handleIntroClose} w="full">
              Get Started
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default CalculatorPage;
